// src/pages/CrearConcurso.tsx

import React, { useState } from "react";
import { createConcurso } from "../api/concursosApi";
import "../css/crear-concurso.css";

interface Documento {
  nombre: string;
  link: string;
}

interface Criterio {
  tipo: string; // "Juicio de Valor" | "Cuantificables Automáticamente"
  subtipo: string; // "Precio" | "Otros"
  descripcion: string; // Descripción del criterio
  ponderacion: number; // Ponderación en porcentaje
}

interface AdjudicatarioItem {
  nif: string;
  name: string;
  cityName: string;
}

interface FormData {
  titulo: string;
  descripcion: string;
  estado: string;
  importe_estimado: string; // numeric
  importe_total: string; // numeric
  fecha_publicacion: string; // date
  enlace: string;
  codigo_expediente: string;
  tipo_contrato: string;
  subtipo_contrato: string;
  duracion: string;
  cpv: string[]; // text[]
  actividad: string[]; // text[]
  codigo_nut: string;
  pais: string;
  entidad_compradora: string;
  entidad_contacto: string;
  direccion: string;
  telefono: string;
  email: string;
  url_perfil_comprador: string;
  documentos: Documento[]; // jsonb
  criterios_adjudicacion: Criterio[]; // jsonb
  resultado_adjudicacion: string; // text
  adjudicatario: AdjudicatarioItem[]; // jsonb
  importe_adjudicacion: string; // numeric
  numero_licitadores: number;
  prorroga: string;
  provincia: string;
  localidad: string;
  fecha_fin_ofertas: string; // date
  fecha_adjudicacion: string; // date
}

const CrearConcurso: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    titulo: "",
    descripcion: "",
    estado: "",
    importe_estimado: "",
    importe_total: "",
    fecha_publicacion: "",
    enlace: "",
    codigo_expediente: "",
    tipo_contrato: "",
    subtipo_contrato: "",
    duracion: "",
    cpv: [""],
    actividad: [], // CAMBIADO: Ahora empieza vacío
    codigo_nut: "",
    pais: "España",
    entidad_compradora: "",
    entidad_contacto: "",
    direccion: "",
    telefono: "",
    email: "",
    url_perfil_comprador: "",
    documentos: [], // CAMBIADO: Ahora empieza vacío
    criterios_adjudicacion: [], // CAMBIADO: Ahora empieza vacío
    resultado_adjudicacion: "",
    adjudicatario: [], // CAMBIADO: Ahora empieza vacío
    importe_adjudicacion: "",
    numero_licitadores: 0,
    prorroga: "",
    provincia: "",
    localidad: "",
    fecha_fin_ofertas: "",
    fecha_adjudicacion: "",
  });

  // Manejadores genéricos para campos "simples"
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Para modificar arrays de string (cpv, actividad) en un índice concreto
  const handleArrayChange = (
    field: keyof FormData,
    index: number,
    value: string
  ) => {
    const arr = [...(formData[field] as string[])];
    arr[index] = value;
    setFormData((prev) => ({ ...prev, [field]: arr }));
  };

  // Agregar elemento vacío a un array
  const addToArray = (field: keyof FormData, item: any) => {
    const arr = [...(formData[field] as any[])];
    arr.push(item);
    setFormData((prev) => ({ ...prev, [field]: arr }));
  };

  // Quitar un elemento
  const removeFromArray = (field: keyof FormData, index: number) => {
    const arr = [...(formData[field] as any[])];
    arr.splice(index, 1);
    setFormData((prev) => ({ ...prev, [field]: arr }));
  };

  // Para JSON arrays como "documentos" o "adjudicatario"...
  const handleJsonArrayChange = (
    field: keyof FormData,
    index: number,
    subField: string,
    value: any
  ) => {
    const arr = [...(formData[field] as any[])];
    arr[index] = { ...arr[index], [subField]: value };
    setFormData((prev) => ({ ...prev, [field]: arr }));
  };

  // Para criterios de adjudicación
  const handleCriterioChange = (
    index: number,
    field: keyof Criterio,
    value: string | number
  ) => {
    const criterios = [...formData.criterios_adjudicacion];

    // Asegurar que el objeto existe
    if (!criterios[index]) {
      criterios[index] = {
        tipo: "",
        subtipo: "",
        descripcion: "",
        ponderacion: 0,
      };
    }

    // Manejar la ponderación de forma especial
    if (field === "ponderacion") {
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      criterios[index] = {
        ...criterios[index],
        [field]: isNaN(numValue) ? 0 : numValue,
      };
    } else {
      criterios[index] = {
        ...criterios[index],
        [field]: value || "",
      };
    }

    setFormData((prev) => ({
      ...prev,
      criterios_adjudicacion: criterios,
    }));
  };

  // Agregar nuevo criterio
  const addCriterio = () => {
    setFormData((prev) => ({
      ...prev,
      criterios_adjudicacion: [
        ...prev.criterios_adjudicacion,
        { tipo: "", subtipo: "", descripcion: "", ponderacion: 0 },
      ],
    }));
  };

  // Eliminar criterio
  const removeCriterio = (index: number) => {
    const criterios = [...formData.criterios_adjudicacion];
    criterios.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      criterios_adjudicacion: criterios,
    }));
  };

  // Función para cancelar y volver atrás
  const handleCancel = () => {
    window.history.back();
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación de campos obligatorios
    if (
      !formData.titulo.trim() ||
      !formData.estado.trim() ||
      !formData.fecha_publicacion.trim() ||
      !formData.enlace.trim() ||
      !formData.codigo_expediente.trim() ||
      !formData.cpv.some((cpvItem) => cpvItem.trim() !== "")
    ) {
      alert(
        "Los campos Título, estado, fecha de publicación, enlace, código expediente y cpv son obligatorios."
      );
      return;
    }

    // Validación de criterios de adjudicación
    const criteriosCompletos = formData.criterios_adjudicacion.filter(
      (criterio) =>
        criterio.descripcion.trim() !== "" &&
        criterio.tipo.trim() !== "" &&
        criterio.subtipo.trim() !== ""
    );

    if (criteriosCompletos.length > 0) {
      // Verificar que todos los criterios completos tengan ponderación
      const criteriosSinPonderacion = criteriosCompletos.filter(
        (criterio) => !criterio.ponderacion || criterio.ponderacion <= 0
      );

      if (criteriosSinPonderacion.length > 0) {
        alert(
          "Todos los criterios completos deben tener una ponderación mayor a 0."
        );
        return;
      }

      // Verificar que la suma de ponderaciones no exceda 100%
      const totalPonderacion = criteriosCompletos.reduce(
        (sum, criterio) => sum + (criterio.ponderacion || 0),
        0
      );

      if (totalPonderacion > 100) {
        alert(
          `La suma de las ponderaciones no puede exceder 100%. Actual: ${totalPonderacion.toFixed(
            2
          )}%`
        );
        return;
      }
    }

    // Limpiar arrays vacíos antes de enviar
    const activiadLimpia = formData.actividad.filter(
      (item) => item.trim() !== ""
    );
    const documentosLimpios = formData.documentos.filter(
      (doc) => doc.nombre.trim() !== "" || doc.link.trim() !== ""
    );
    const adjudicatariosLimpios = formData.adjudicatario.filter(
      (adj) =>
        adj.nif.trim() !== "" ||
        adj.name.trim() !== "" ||
        adj.cityName.trim() !== ""
    );

    const formDataToSend = {
      ...formData,
      actividad: activiadLimpia,
      documentos: documentosLimpios,
      criterios_adjudicacion: criteriosCompletos,
      adjudicatario: adjudicatariosLimpios,
    };

    try {
      await createConcurso(formDataToSend);
      alert("Concurso creado con éxito.");
      // Podrías resetear el formulario o navegar
    } catch (error) {
      console.error("Error al crear el concurso:", error);
      alert("Error al crear el concurso.");
    }
  };

  return (
    <div className="cc-container">
      <h1>Crear Nuevo Concurso</h1>
      <form className="cc-form" onSubmit={handleSubmit}>
        {/* Título */}
        <div className="cc-form-group">
          <label>Título</label>
          <input
            type="text"
            name="titulo"
            value={formData.titulo}
            onChange={handleChange}
            required
          />
        </div>

        {/* Descripción */}
        <div className="cc-form-group cc-full-width">
          <label>Descripción</label>
          <textarea
            name="descripcion"
            value={formData.descripcion}
            onChange={handleChange}
          ></textarea>
        </div>

        {/* Estado */}
        <div className="cc-form-group">
          <label>Estado</label>
          <select
            name="estado"
            value={formData.estado}
            onChange={handleChange}
            required
          >
            <option value="">(Seleccione)</option>
            <option value="Anuncio previo">Anuncio previo</option>
            <option value="En plazo">En plazo</option>
            <option value="Pdte adjudicación">Pdte adjudicación</option>
            <option value="Adjudicada">Adjudicada</option>
            <option value="Resuelta">Resuelta</option>
            <option value="Anulada">Anulada</option>
          </select>
        </div>

        {/* Importe estimado */}
        <div className="cc-form-group">
          <label>Importe Estimado</label>
          <input
            type="number"
            name="importe_estimado"
            value={formData.importe_estimado}
            onChange={handleChange}
          />
        </div>

        {/* Importe total */}
        <div className="cc-form-group">
          <label>Importe Total</label>
          <input
            type="number"
            name="importe_total"
            value={formData.importe_total}
            onChange={handleChange}
          />
        </div>

        {/* Fecha de Publicación */}
        <div className="cc-form-group">
          <label>Fecha Publicación</label>
          <input
            type="date"
            name="fecha_publicacion"
            value={formData.fecha_publicacion}
            onChange={handleChange}
            required
          />
        </div>

        {/* Fecha Fin Ofertas */}
        <div className="cc-form-group">
          <label>Fecha Fin Ofertas</label>
          <input
            type="datetime-local"
            name="fecha_fin_ofertas"
            value={formData.fecha_fin_ofertas}
            onChange={handleChange}
          />
        </div>

        {/* Fecha Adjudicación */}
        <div className="cc-form-group">
          <label>Fecha Adjudicación</label>
          <input
            type="date"
            name="fecha_adjudicacion"
            value={formData.fecha_adjudicacion}
            onChange={handleChange}
          />
        </div>

        {/* Enlace */}
        <div className="cc-form-group">
          <label>Enlace (URL)</label>
          <input
            type="text"
            name="enlace"
            value={formData.enlace}
            onChange={handleChange}
            required
          />
        </div>

        {/* Código Expediente */}
        <div className="cc-form-group">
          <label>Código Expediente</label>
          <input
            type="text"
            name="codigo_expediente"
            value={formData.codigo_expediente}
            onChange={handleChange}
            required
          />
        </div>

        {/* Tipo Contrato */}
        <div className="cc-form-group">
          <label>Tipo Contrato</label>
          <input
            type="text"
            name="tipo_contrato"
            value={formData.tipo_contrato}
            onChange={handleChange}
          />
        </div>

        {/* Subtipo Contrato */}
        <div className="cc-form-group">
          <label>Subtipo Contrato</label>
          <input
            type="text"
            name="subtipo_contrato"
            value={formData.subtipo_contrato}
            onChange={handleChange}
          />
        </div>

        {/* Duración */}
        <div className="cc-form-group">
          <label>Duración</label>
          <input
            type="text"
            name="duracion"
            value={formData.duracion}
            onChange={handleChange}
          />
        </div>

        {/* CPV (array de strings) */}
        <div className="cc-form-group cc-full-width">
          <label>CPV (varios):</label>
          <div className="cc-array-container">
            {formData.cpv.map((cpvItem, idx) => (
              <div key={idx} className="cc-array-item">
                <input
                  type="text"
                  value={cpvItem}
                  onChange={(e) =>
                    handleArrayChange("cpv", idx, e.target.value)
                  }
                  required
                />
                <button
                  type="button"
                  className="cc-delete-button"
                  onClick={() => removeFromArray("cpv", idx)}
                >
                  Eliminar
                </button>
              </div>
            ))}
            <button
              type="button"
              className="cc-add-button"
              onClick={() => addToArray("cpv", "")}
            >
              Añadir CPV
            </button>
          </div>
        </div>

        {/* Actividad (array de strings) - CORREGIDO: Solo muestra si hay elementos */}
        <div className="cc-form-group cc-full-width">
          <label>Actividad (varias):</label>
          <div className="cc-array-container">
            {formData.actividad.map((actItem, idx) => (
              <div key={idx} className="cc-array-item">
                <input
                  type="text"
                  value={actItem}
                  onChange={(e) =>
                    handleArrayChange("actividad", idx, e.target.value)
                  }
                />
                <button
                  type="button"
                  className="cc-delete-button"
                  onClick={() => removeFromArray("actividad", idx)}
                >
                  Eliminar
                </button>
              </div>
            ))}
            <button
              type="button"
              className="cc-add-button"
              onClick={() => addToArray("actividad", "")}
            >
              Añadir Actividad
            </button>
          </div>
        </div>

        {/* Código NUT */}
        <div className="cc-form-group">
          <label>Código NUT</label>
          <input
            type="text"
            name="codigo_nut"
            value={formData.codigo_nut}
            onChange={handleChange}
          />
        </div>

        {/* País */}
        <div className="cc-form-group">
          <label>País</label>
          <input
            type="text"
            name="pais"
            value={formData.pais}
            onChange={handleChange}
          />
        </div>

        {/* Provincia */}
        <div className="cc-form-group">
          <label>Provincia</label>
          <input
            type="text"
            name="provincia"
            value={formData.provincia}
            onChange={handleChange}
          />
        </div>

        {/* Localidad */}
        <div className="cc-form-group">
          <label>Localidad</label>
          <input
            type="text"
            name="localidad"
            value={formData.localidad}
            onChange={handleChange}
          />
        </div>

        {/* Entidad Compradora */}
        <div className="cc-form-group">
          <label>Entidad Compradora</label>
          <input
            type="text"
            name="entidad_compradora"
            value={formData.entidad_compradora}
            onChange={handleChange}
          />
        </div>

        {/* Entidad Contacto */}
        <div className="cc-form-group">
          <label>Entidad Contacto</label>
          <input
            type="text"
            name="entidad_contacto"
            value={formData.entidad_contacto}
            onChange={handleChange}
          />
        </div>

        {/* Dirección */}
        <div className="cc-form-group">
          <label>Dirección</label>
          <input
            type="text"
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
          />
        </div>

        {/* Teléfono */}
        <div className="cc-form-group">
          <label>Teléfono</label>
          <input
            type="text"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
          />
        </div>

        {/* Email */}
        <div className="cc-form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        {/* URL perfil comprador */}
        <div className="cc-form-group">
          <label>URL Perfil Comprador</label>
          <input
            type="text"
            name="url_perfil_comprador"
            value={formData.url_perfil_comprador}
            onChange={handleChange}
          />
        </div>

        {/* Documentos - CORREGIDO: Solo muestra si hay elementos */}
        <div className="cc-form-group cc-full-width">
          <label>Documentos:</label>
          <div className="cc-multiple-items">
            {formData.documentos.map((doc, idx) => (
              <div key={idx} className="cc-doc-item">
                <input
                  type="text"
                  placeholder="Nombre"
                  value={doc.nombre}
                  onChange={(e) =>
                    handleJsonArrayChange(
                      "documentos",
                      idx,
                      "nombre",
                      e.target.value
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Link"
                  value={doc.link}
                  onChange={(e) =>
                    handleJsonArrayChange(
                      "documentos",
                      idx,
                      "link",
                      e.target.value
                    )
                  }
                />
                <button
                  type="button"
                  className="cc-delete-button"
                  onClick={() => removeFromArray("documentos", idx)}
                >
                  Eliminar
                </button>
              </div>
            ))}
            <button
              type="button"
              className="cc-add-button"
              onClick={() => addToArray("documentos", { nombre: "", link: "" })}
            >
              Añadir Documento
            </button>
          </div>
        </div>

        {/* Criterios de Adjudicación - CORREGIDO: Solo muestra si hay elementos + inputs libres */}
        <div className="cc-form-group cc-full-width">
          <label>Criterios de Adjudicación</label>
          <div className="cc-multiple-items">
            {formData.criterios_adjudicacion.map((criterio, index) => (
              <div key={index} className="cc-criterio-item">
                <div className="cc-criterio-fields">
                  <div className="cc-field">
                    <label>Tipo:</label>
                    <input
                      type="text"
                      placeholder="Ej: Juicio de Valor"
                      value={criterio.tipo || ""}
                      onChange={(e) =>
                        handleCriterioChange(index, "tipo", e.target.value)
                      }
                    />
                  </div>

                  <div className="cc-field">
                    <label>Subtipo:</label>
                    <input
                      type="text"
                      placeholder="Ej: Precio, Otros"
                      value={criterio.subtipo || ""}
                      onChange={(e) =>
                        handleCriterioChange(index, "subtipo", e.target.value)
                      }
                    />
                  </div>

                  <div className="cc-field">
                    <label>Ponderación (%):</label>
                    <input
                      type="number"
                      placeholder="0"
                      min="0"
                      max="100"
                      step="0.01"
                      value={criterio.ponderacion || ""}
                      onChange={(e) =>
                        handleCriterioChange(
                          index,
                          "ponderacion",
                          e.target.value ? parseFloat(e.target.value) : 0
                        )
                      }
                    />
                  </div>
                </div>

                <div className="cc-field cc-full-width-field">
                  <label>Descripción:</label>
                  <input
                    type="text"
                    placeholder="Descripción del criterio"
                    value={criterio.descripcion || ""}
                    onChange={(e) =>
                      handleCriterioChange(index, "descripcion", e.target.value)
                    }
                  />
                </div>

                {/* Vista previa del criterio */}
                <div className="cc-criterio-preview">
                  <strong>
                    {criterio.descripcion || "Sin descripción"}(
                    {criterio.tipo || "Sin tipo"} -{" "}
                    {criterio.subtipo || "Sin subtipo"}):
                    {criterio.ponderacion || 0}%
                  </strong>
                </div>

                <button
                  type="button"
                  className="cc-delete-button"
                  onClick={() => removeCriterio(index)}
                >
                  Eliminar
                </button>
              </div>
            ))}

            <button
              type="button"
              className="cc-add-button"
              onClick={addCriterio}
            >
              Añadir Criterio
            </button>

            {/* Validación de ponderaciones */}
            {formData.criterios_adjudicacion.length > 0 &&
              (() => {
                const totalPonderacion = formData.criterios_adjudicacion.reduce(
                  (sum, criterio) => sum + (criterio.ponderacion || 0),
                  0
                );

                if (totalPonderacion > 100) {
                  return (
                    <div className="cc-validation-warning">
                      ⚠️ La suma de ponderaciones excede 100% (actual:{" "}
                      {totalPonderacion.toFixed(2)}%)
                    </div>
                  );
                } else if (totalPonderacion > 0 && totalPonderacion < 100) {
                  return (
                    <div className="cc-validation-info">
                      ℹ️ Suma actual de ponderaciones:{" "}
                      {totalPonderacion.toFixed(2)}% (faltan{" "}
                      {(100 - totalPonderacion).toFixed(2)}%)
                    </div>
                  );
                } else if (totalPonderacion === 100) {
                  return (
                    <div className="cc-validation-success">
                      ✅ Ponderaciones correctas: 100%
                    </div>
                  );
                }
                return null;
              })()}
          </div>
        </div>

        {/* Resultado Adjudicacion */}
        <div className="cc-form-group">
          <label>Resultado Adjudicación</label>
          <input
            type="text"
            name="resultado_adjudicacion"
            value={formData.resultado_adjudicacion}
            onChange={handleChange}
          />
        </div>

        {/* Adjudicatario - CORREGIDO: Solo muestra si hay elementos */}
        <div className="cc-form-group cc-full-width">
          <label>Adjudicatarios</label>
          <div className="cc-multiple-items">
            {formData.adjudicatario.map((item, idx) => (
              <div key={idx} className="cc-adjudicatario-item">
                <input
                  type="text"
                  placeholder="NIF"
                  value={item.nif}
                  onChange={(e) =>
                    handleJsonArrayChange(
                      "adjudicatario",
                      idx,
                      "nif",
                      e.target.value
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Nombre"
                  value={item.name}
                  onChange={(e) =>
                    handleJsonArrayChange(
                      "adjudicatario",
                      idx,
                      "name",
                      e.target.value
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Ciudad"
                  value={item.cityName}
                  onChange={(e) =>
                    handleJsonArrayChange(
                      "adjudicatario",
                      idx,
                      "cityName",
                      e.target.value
                    )
                  }
                />
                <button
                  type="button"
                  className="cc-delete-button"
                  onClick={() => removeFromArray("adjudicatario", idx)}
                >
                  Eliminar
                </button>
              </div>
            ))}
            <button
              type="button"
              className="cc-add-button"
              onClick={() =>
                addToArray("adjudicatario", { nif: "", name: "", cityName: "" })
              }
            >
              Añadir Adjudicatario
            </button>
          </div>
        </div>

        {/* Importe Adjudicación */}
        <div className="cc-form-group">
          <label>Importe Adjudicación</label>
          <input
            type="number"
            name="importe_adjudicacion"
            value={formData.importe_adjudicacion}
            onChange={handleChange}
          />
        </div>

        {/* Número de licitadores */}
        <div className="cc-form-group">
          <label>Número de Licitadores</label>
          <input
            type="number"
            name="numero_licitadores"
            value={formData.numero_licitadores}
            onChange={handleChange}
          />
        </div>

        {/* Prórroga */}
        <div className="cc-form-group cc-full-width">
          <label>Prórroga</label>
          <textarea
            name="prorroga"
            value={formData.prorroga}
            onChange={handleChange}
          />
        </div>

        {/* Botones: Cancelar y Submit */}
        <div className="cc-form-group cc-button-group">
          <button
            type="button"
            onClick={handleCancel}
            className="cc-cancel-button"
          >
            Cancelar
          </button>
          <button type="submit" className="cc-submit-button">
            Crear Concurso
          </button>
        </div>
      </form>
    </div>
  );
};

export default CrearConcurso;
